<script setup lang="ts">
import InputError from '@/components/InputError.vue';
import InputLabel from '@/components/InputLabel.vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ref } from 'vue';
import { useUpdatePassword } from '@/composables/api/useProfileApi';
import { AxiosError } from 'axios';

interface PasswordFormErrors {
  current_password?: string[];
  password?: string[];
  password_confirmation?: string[];
  [key: string]: string[] | undefined;
}

const { mutate: updatePassword, isPending: isUpdatingPassword } = useUpdatePassword();

const errors = ref<PasswordFormErrors>({});
const passwordInput = ref('');
const currentPasswordInput = ref('');
const passwordConfirmation = ref('');

function resetPasswordFields() {
  passwordInput.value = '';
  currentPasswordInput.value = '';
  passwordConfirmation.value = '';
}

function updatePasswordHandler() {
  updatePassword(
    {
      current_password: currentPasswordInput.value,
      password: passwordInput.value,
      password_confirmation: passwordConfirmation.value
    },
    {
      onSuccess: () => {
        errors.value = {};
        resetPasswordFields();
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError && error.response?.status === 422) {
          errors.value = error.response.data.errors;
        } else {
          errors.value = {};
        }
      }
    }
  );
}
</script>

<template>
  <section>
    <header>
      <h2 class="text-lg font-medium">Update Password</h2>

      <p class="mt-1 text-sm">
        Ensure your account is using a long, random password to stay secure.
      </p>
    </header>

    <form class="mt-6 space-y-6" @submit.prevent="updatePasswordHandler">
      <div>
        <InputLabel for="current_password" value="Current Password" />

        <Input
          id="current_password"
          type="password"
          class="mt-1 block w-full"
          :modelValue="currentPasswordInput"
          @update:modelValue="(value: string | number) => currentPasswordInput = String(value)"
          autocomplete="new-password"
          aria-autocomplete="none"
          :disabled="isUpdatingPassword"
        />

        <InputError
          v-if="errors['current_password']"
          :message="errors['current_password'][0]"
          class="mt-2"
        />
      </div>

      <div>
        <InputLabel for="password" value="New Password" />

        <Input 
          id="password" 
          type="password" 
          class="mt-1 w-full" 
          :modelValue="passwordInput"
          @update:modelValue="(value: string | number) => passwordInput = String(value)"
          :disabled="isUpdatingPassword"
        />

        <InputError v-if="errors['password']" :message="errors['password'][0]" class="mt-2" />
      </div>

      <div>
        <InputLabel for="password_confirmation" value="Confirm Password" />

        <Input
          id="password_confirmation"
          type="password"
          class="mt-1 w-full"
          :modelValue="passwordConfirmation"
          @update:modelValue="(value: string | number) => passwordConfirmation = String(value)"
          :disabled="isUpdatingPassword"
        />

        <InputError
          v-if="errors['password_confirmation']"
          :message="errors['password_confirmation'][0]"
          class="mt-2"
        />
      </div>

      <div class="flex items-center gap-4">
        <Button 
          type="submit"
          :disabled="isUpdatingPassword"
          :loading="isUpdatingPassword"
        >
          Save
        </Button>
      </div>
    </form>
  </section>
</template>
